import { VOICE_AGENT_VOICES, VoiceAgentVoice } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { useMemo } from "react";
import aaronSample from "../assets/aaron.wav";
import adamSample from "../assets/adam.wav";
import alanSample from "../assets/alan.wav";
import alexanderSample from "../assets/alexander.wav";
import aliceSample from "../assets/alice.wav";
import amberSample from "../assets/amber.wav";
import annaSample from "../assets/anna.wav";
import arthurSample from "../assets/arthur.wav";
import charlesSample from "../assets/charles.wav";
import chloeSample from "../assets/chloe.wav";
import dianaSample from "../assets/diana.wav";
import emmaSample from "../assets/emma.wav";
import ethanSample from "../assets/ethan.wav";
import felixSample from "../assets/felix.wav";
import ianSample from "../assets/ian.wav";
import katherineSample from "../assets/katherine.wav";
import lauraSample from "../assets/laura.wav";
import lilySample from "../assets/lily.wav";
import missySample from "../assets/missy.wav";
import oliverSample from "../assets/oliver.wav";
import owenSample from "../assets/owen.wav";
import paulSample from "../assets/paul.wav";
import peterSample from "../assets/peter.wav";
import randySample from "../assets/randy.wav";
import samuelSample from "../assets/samuel.wav";
import simonSample from "../assets/simon.wav";
import victoriaSample from "../assets/victoria.wav";
import violetSample from "../assets/violet.wav";
import zachSample from "../assets/zach.wav";
import zoeSample from "../assets/zoe.wav";
import { VoiceAgentVoiceDetails } from "../types";

export const useVoiceCatalog = (): VoiceAgentVoiceDetails[] => {
  const { t } = useTranslations();

  return useMemo(() => {
    // Keyed by voice so that every value the API accepts is guaranteed to have
    // a card - adding a voice to `VOICE_AGENT_VOICES` without describing it here
    // is a type error rather than a voice silently missing from the picker
    const voiceDetailsByVoice: Record<
      VoiceAgentVoice,
      Omit<VoiceAgentVoiceDetails, "id">
    > = {
      Zoe: {
        gender: "female",
        style: "bright",
        sound: zoeSample,
        description: t({
          id: "client-support-page.voice-zoe-description",
          defaultMessage: "Bright, energetic, and highly engaging.",
        }),
      },
      Anna: {
        gender: "female",
        style: "bright",
        sound: annaSample,
        description: t({
          id: "client-support-page.voice-anna-description",
          defaultMessage: "Crisp, fast-paced, and highly articulate.",
        }),
      },
      Peter: {
        gender: "male",
        style: "bright",
        sound: peterSample,
        description: t({
          id: "client-support-page.voice-peter-description",
          defaultMessage: "Friendly, approachable, 'guy next door' tone.",
        }),
      },
      Victoria: {
        gender: "female",
        style: "bright",
        sound: victoriaSample,
        description: t({
          id: "client-support-page.voice-victoria-description",
          defaultMessage: "Vibrant, cheerful, and welcoming.",
        }),
      },
      Katherine: {
        gender: "female",
        style: "firm",
        sound: katherineSample,
        description: t({
          id: "client-support-page.voice-katherine-description",
          defaultMessage: "Confident, direct, and highly professional.",
        }),
      },
      Oliver: {
        gender: "male",
        style: "firm",
        sound: oliverSample,
        description: t({
          id: "client-support-page.voice-oliver-description",
          defaultMessage: "Deep, mature, and deeply authoritative.",
        }),
      },
      Alexander: {
        gender: "male",
        style: "firm",
        sound: alexanderSample,
        description: t({
          id: "client-support-page.voice-alexander-description",
          defaultMessage: "Steady, commanding, and resolute tone.",
        }),
      },
      Owen: {
        gender: "male",
        style: "easy-going",
        sound: owenSample,
        description: t({
          id: "client-support-page.voice-owen-description",
          defaultMessage: "Relaxed, casual, and conversational style.",
        }),
      },
      Chloe: {
        gender: "female",
        style: "easy-going",
        sound: chloeSample,
        description: t({
          id: "client-support-page.voice-chloe-description",
          defaultMessage: "Comfortable, warm, and highly informal.",
        }),
      },
      Emma: {
        gender: "female",
        style: "clear",
        sound: emmaSample,
        description: t({
          id: "client-support-page.voice-emma-description",
          defaultMessage: "Sharp, precise, and perfectly articulated.",
        }),
      },
      Ian: {
        gender: "male",
        style: "clear",
        sound: ianSample,
        description: t({
          id: "client-support-page.voice-ian-description",
          defaultMessage: "Transparent, neutral, and evenly paced.",
        }),
      },
      Lily: {
        gender: "female",
        style: "upbeat",
        sound: lilySample,
        description: t({
          id: "client-support-page.voice-lily-description",
          defaultMessage: "Positive, animated, and enthusiastically tone.",
        }),
      },
      Samuel: {
        gender: "male",
        style: "even",
        sound: samuelSample,
        description: t({
          id: "client-support-page.voice-samuel-description",
          defaultMessage: "Balanced, uniform, and deeply consistent.",
        }),
      },
      Arthur: {
        gender: "male",
        style: "friendly",
        sound: arthurSample,
        description: t({
          id: "client-support-page.voice-arthur-description",
          defaultMessage: "Comforting, supportive, and kind.",
        }),
      },
      Adam: {
        gender: "male",
        style: "lively",
        sound: adamSample,
        description: t({
          id: "client-support-page.voice-adam-description",
          defaultMessage: "Vivid yet measured and professional.",
        }),
      },
      Felix: {
        gender: "male",
        style: "excitable",
        sound: felixSample,
        description: t({
          id: "client-support-page.voice-felix-description",
          defaultMessage: "Highly energetic and dynamically expressive.",
        }),
      },
      Alice: {
        gender: "female",
        style: "breezy",
        sound: aliceSample,
        description: t({
          id: "client-support-page.voice-alice-description",
          defaultMessage: "Natural, flowing narration style.",
        }),
      },
      Ethan: {
        gender: "male",
        style: "smooth",
        sound: ethanSample,
        description: t({
          id: "client-support-page.voice-ethan-description",
          defaultMessage: "Soothing, fluid, and completely calm.",
        }),
      },
      Aaron: {
        gender: "male",
        style: "smooth",
        sound: aaronSample,
        description: t({
          id: "client-support-page.voice-aaron-description",
          defaultMessage: "Deep, velvety, and deeply resonant.",
        }),
      },
      Diana: {
        gender: "female",
        style: "smooth",
        sound: dianaSample,
        description: t({
          id: "client-support-page.voice-diana-description",
          defaultMessage: "Sleek, polished, and exceptionally professional.",
        }),
      },
      Alan: {
        gender: "male",
        style: "gravelly",
        sound: alanSample,
        description: t({
          id: "client-support-page.voice-alan-description",
          defaultMessage: "Husky, textured, and uniquely characteristic.",
        }),
      },
      Amber: {
        gender: "female",
        style: "soft",
        sound: amberSample,
        description: t({
          id: "client-support-page.voice-amber-description",
          defaultMessage: "Gentle, quiet, and intimately presented.",
        }),
      },
      Missy: {
        gender: "female",
        style: "mature",
        sound: missySample,
        description: t({
          id: "client-support-page.voice-missy-description",
          defaultMessage: "Seasoned, elder, and highly trustworthy.",
        }),
      },
      Zach: {
        gender: "male",
        style: "casual",
        sound: zachSample,
        description: t({
          id: "client-support-page.voice-zach-description",
          defaultMessage: "Everyday, deeply colloquial speech pattern.",
        }),
      },
      Simon: {
        gender: "male",
        style: "knowledgeable",
        sound: simonSample,
        description: t({
          id: "client-support-page.voice-simon-description",
          defaultMessage: "Academic, authoritative, and instructive.",
        }),
      },
      Charles: {
        gender: "male",
        style: "informative",
        sound: charlesSample,
        description: t({
          id: "client-support-page.voice-charles-description",
          defaultMessage: "Clear, matter-of-fact reporter tone.",
        }),
      },
      Randy: {
        gender: "male",
        style: "informative",
        sound: randySample,
        description: t({
          id: "client-support-page.voice-randy-description",
          defaultMessage: "Direct, professional news broadcasting style.",
        }),
      },
      Laura: {
        gender: "female",
        style: "youthful",
        sound: lauraSample,
        description: t({
          id: "client-support-page.voice-laura-description",
          defaultMessage: "Composed, companionable, and warm-spirited.",
        }),
      },
      Paul: {
        gender: "male",
        style: "forward",
        sound: paulSample,
        description: t({
          id: "client-support-page.voice-paul-description",
          defaultMessage: "Assertive, fast, and highly prompt communication.",
        }),
      },
      Violet: {
        gender: "female",
        style: "gentle",
        sound: violetSample,
        description: t({
          id: "client-support-page.voice-violet-description",
          defaultMessage:
            "Kind, patient, and softly rounded conversationalist.",
        }),
      },
    };

    return VOICE_AGENT_VOICES.map((voice) => ({
      id: voice,
      ...voiceDetailsByVoice[voice],
    }));
  }, [t]);
};
